import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { Town, TownUpdatePayload, TownDevice } from '@shared/types/town';
import type { SettingLog, CSLog, LogCreatePayload } from '@shared/types/log';

// ─── Town Detail ───
async function fetchTownDetail(id: number): Promise<Town> {
  const { data } = await api.get<Town>(`/towns/${id}`);
  return data;
}

export function useTownDetail(townId: number | null) {
  return useQuery({
    queryKey: ['town', townId],
    queryFn: () => fetchTownDetail(townId!),
    enabled: townId !== null,
  });
}

// ─── Town Update (PATCH /api/towns/:id) ───
export function useTownUpdate(townId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TownUpdatePayload) => {
      const { data } = await api.patch(`/towns/${townId}`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['town', townId] });
      qc.invalidateQueries({ queryKey: ['towns'] });
    },
  });
}

// ─── Town Status Change (PATCH /api/towns/:id/status) ───
export function useTownStatusChange(townId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (status: 'active' | 'excluded') => {
      const { data } = await api.patch(`/towns/${townId}/status`, { status });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['town', townId] });
      qc.invalidateQueries({ queryKey: ['towns'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

// ─── Town Delete (DELETE /api/towns/:id) ───
export function useTownDelete(townId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete(`/towns/${townId}`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['towns'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

// ─── Devices ───
async function fetchDevices(townId: number): Promise<TownDevice[]> {
  const { data } = await api.get<TownDevice[]>(`/towns/${townId}/devices`);
  return data;
}

export function useTownDevices(townId: number | null) {
  return useQuery({
    queryKey: ['town-devices', townId],
    queryFn: () => fetchDevices(townId!),
    enabled: townId !== null,
  });
}

export function useSaveDevices(townId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (devices: TownDevice[]) => {
      const { data } = await api.put(`/towns/${townId}/devices`, { devices });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['town-devices', townId] });
    },
  });
}

// ─── Contract ───
export function useContractUpdate(townId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await api.patch(`/towns/${townId}/contract`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['town', townId] });
      qc.invalidateQueries({ queryKey: ['towns'] });
    },
  });
}

export function useContractUpload(townId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/towns/${townId}/contract/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['town', townId] });
    },
  });
}

// ─── Setting Logs ───
async function fetchSettingLogs(townId: number): Promise<SettingLog[]> {
  const { data } = await api.get<SettingLog[]>(`/towns/${townId}/setting-logs`);
  return data;
}

export function useSettingLogs(townId: number | null) {
  return useQuery({
    queryKey: ['setting-logs', townId],
    queryFn: () => fetchSettingLogs(townId!),
    enabled: townId !== null,
  });
}

export function useAddSettingLog(townId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: LogCreatePayload) => {
      const { data } = await api.post(`/towns/${townId}/setting-logs`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['setting-logs', townId] });
    },
  });
}

// ─── CS Logs ───
async function fetchCSLogs(townId: number): Promise<CSLog[]> {
  const { data } = await api.get<CSLog[]>(`/towns/${townId}/cs-logs`);
  return data;
}

export function useCSLogs(townId: number | null) {
  return useQuery({
    queryKey: ['cs-logs', townId],
    queryFn: () => fetchCSLogs(townId!),
    enabled: townId !== null,
  });
}

export function useAddCSLog(townId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: LogCreatePayload) => {
      const { data } = await api.post(`/towns/${townId}/cs-logs`, payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cs-logs', townId] });
    },
  });
}
